import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Zap, BatteryCharging, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Power = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/power"]?.title} description={seoData["industries/power"]?.description} path="/industries/power" keywords={seoData["industries/power"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Power Generation"
        description="Industrial coating contractor for power generation facilities. Cooling tower coating, condenser repair, boiler tube protection, and corrosion prevention for gas-fired, coal, nuclear, and renewable power plants across California."
        backgroundKey="power"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Cooling tower fill degradation, basin corrosion, and structural deterioration',
              'Condenser tube sheet corrosion and cooling water inlet erosion',
              'Boiler tube erosion and high-temperature corrosion',
              'FGD scrubber lining failure from acidic flue gas condensate',
              'Circulating water pipe corrosion and concrete degradation',
              'Turbine casing wear and bearing housing damage'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-yellow-50 rounded-lg">
                <Zap className="text-yellow-700 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-yellow-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BatteryCharging className="text-yellow-600 mr-3 flex-shrink-0" size={22} />
                  Cooling Tower Restoration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Complete cooling tower rehabilitation including basin coating, structural concrete repair, fill media replacement, and distribution deck restoration. Extend cooling tower service life and improve thermal performance.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BatteryCharging className="text-yellow-600 mr-3 flex-shrink-0" size={22} />
                  Condenser & Heat Exchanger Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Tube sheet coating, inlet waterbox lining, and tube-to-tubesheet seal repair using epoxy systems rated for immersion service. Restore heat transfer efficiency and prevent tube failures.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BatteryCharging className="text-yellow-600 mr-3 flex-shrink-0" size={22} />
                  High-Temperature Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Specialized coating systems for boiler components, economizers, and exhaust systems operating at elevated temperatures. Erosion and corrosion protection for coal, gas, and biomass-fired units.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BatteryCharging className="text-yellow-600 mr-3 flex-shrink-0" size={22} />
                  Circulating Water System Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Pipe coating and repair for circulating water, service water, and fire protection systems. Concrete repair for intake structures, discharge canals, and below-grade piping.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Power Generation?</h2>
              <div className="space-y-3">
                {['Outage-optimized scheduling — work scope planned around unit outages and maintenance windows',
                  'No hot work in plant environments — cold-applied repair systems',
                  'Extend equipment service life and defer capital replacement projects',
                  'OSHA 10/30 trained crews with power plant safety orientation',
                  'Rapid cure systems available for time-critical outage work',
                  'Manufacturer-backed warranties on all installed systems'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-yellow-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Power Plant Specialists</h3>
              <p className="text-yellow-100 mb-6">BCS has completed projects at gas-fired, combined cycle, and renewable power generation facilities across California. Our crews are trained in power plant safety protocols and outage scheduling.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/hvac-repair')}>
                View HVAC Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Power Generation Assets</h2>
          <p className="text-blue-100 mb-8">Contact our power generation specialists for a free assessment during your next planned outage.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Power;
